import { Eye, Loader2, Palette, Sparkles, Type } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { getPlatformName } from "../../utils/platformIcons";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Select from "../ui/Select";

export interface PlatformSuggestion {
  visual_concept?: string;
  detailed_prompt?: string;
  color_palette?: string[];
  key_elements?: string[];
  composition_style?: string;
  text_overlay_suggestion?: string;
  emotional_tone?: string;
  technical_specs?: string;
  error?: string;
  platform?: string;
  fallback?: boolean;
  [key: string]: any;
}

export interface VisualSuggestionsResult {
  success?: boolean;
  suggestions?: Record<string, PlatformSuggestion>;
  generated_at?: string;
  platforms_analyzed?: string[];
}

/** Modelos OpenAI para sugestões visuais (backend usa API OpenAI) */
export const VISUAL_SUGGESTIONS_MODELS = [
  { value: "gpt-5.4-nano", label: "GPT-5.4 Nano — padrão e performático" },
] as const;

interface VisualSuggestionsModalProps {
  open: boolean;
  onClose: () => void;
  postId: number | null;
  data: VisualSuggestionsResult | null | undefined;
  onGenerate: (postId: number, model: string) => Promise<void>;
  loading?: boolean;
}

const formatPlatformLabel = (platform: string) => getPlatformName(platform);

/** Placeholder de exemplo para quando ainda não há sugestões geradas */
const PlaceholderExample = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 rounded-xl border border-dashed border-purple-300 bg-gray-50/80 p-5">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
        <Sparkles className="h-4 w-4 text-amber-500" />
        {t("Exemplo do que você verá após gerar as sugestões")}
      </div>
      <p className="text-xs text-gray-500">
        {t(
          'A IA analisará o conteúdo do post e sugerirá conceitos visuais, paleta de cores, elementos e prompts por plataforma. Clique em "Gerar sugestões" para começar.',
        )}
      </p>

      <div className="space-y-4">
        <div className="rounded-lg border border-purple-200 bg-white p-4 shadow-xs space-y-3 opacity-90">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 border-b border-purple-200 pb-2">
            <Eye className="h-4 w-4 text-teal-600" />
            {t("LinkedIn (exemplo)")}
          </h3>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("Conceito visual")}
            </span>
            <p className="text-sm text-gray-600 mt-0.5 italic">
              {t("Imagem corporativa com gráficos e tom profissional.")}
            </p>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <Palette className="h-3.5 w-3.5" /> {t("Paleta de cores")}
            </span>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {[t("Azul corporativo"), t("Cinza"), t("Branco")].map(
                (color, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white border border-purple-200 text-gray-600"
                  >
                    {color}
                  </span>
                ),
              )}
            </div>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <Type className="h-3.5 w-3.5" />{" "}
              {t("Texto sugerido para overlay")}
            </span>
            <p className="text-sm text-gray-600 mt-0.5 italic">
              {t("Destaque o principal benefício em uma frase.")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const VisualSuggestionsModal: React.FC<VisualSuggestionsModalProps> = ({
  open,
  onClose,
  postId,
  data,
  onGenerate,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [selectedModel, setSelectedModel] = useState<string>(
    VISUAL_SUGGESTIONS_MODELS[0].value,
  );

  const suggestions = data?.suggestions ?? {};
  const platforms = data?.platforms_analyzed ?? Object.keys(suggestions);
  const generatedAt = data?.generated_at;
  const hasData = platforms.length > 0 && Object.keys(suggestions).length > 0;

  const handleGenerate = () => {
    if (postId == null) return;
    onGenerate(postId, selectedModel);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnOverlayClick
      title={t("Sugestões visuais — Post #{postId}", { postId: postId ?? "" })}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleGenerate}
            disabled={loading || postId == null}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                {t("Gerando…")}
              </>
            ) : (
              t("Gerar sugestões")
            )}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            {t("Fechar")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3 pb-2 border-b border-purple-200">
          <div className="min-w-[280px] flex-1">
            <Select
              label={t("Modelo de IA para gerar sugestões")}
              options={VISUAL_SUGGESTIONS_MODELS.map((m) => ({
                value: m.value,
                label: t(m.label),
              }))}
              value={selectedModel}
              onChange={setSelectedModel}
              size="md"
              disabled={loading}
            />
          </div>
        </div>

        {generatedAt && (
          <p className="text-xs text-gray-500">
            {t("Última geração: {date}", {
              date: new Date(generatedAt).toLocaleString("pt-BR"),
            })}
          </p>
        )}

        {!hasData ? (
          <PlaceholderExample />
        ) : (
          <div className="space-y-6">
            {platforms.map((platformKey) => {
              const s = suggestions[platformKey];
              if (!s) return null;
              if (s.error) {
                return (
                  <div
                    key={platformKey}
                    className="rounded-lg border border-red-200 bg-red-50 p-4"
                  >
                    <h3 className="text-sm font-semibold text-red-800 mb-1">
                      {formatPlatformLabel(platformKey)}
                    </h3>
                    <p className="text-sm text-red-700">{s.error}</p>
                  </div>
                );
              }
              return (
                <div
                  key={platformKey}
                  className="rounded-lg border border-purple-200 bg-gray-50/50 p-4 space-y-3"
                >
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 border-b border-purple-200 pb-2">
                    <Eye className="h-4 w-4 text-teal-600" />
                    {formatPlatformLabel(platformKey)}
                    {s.fallback && (
                      <span className="text-xs font-normal text-amber-600">
                        {t("(análise simplificada)")}
                      </span>
                    )}
                  </h3>

                  {s.visual_concept && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("Conceito visual")}
                      </span>
                      <p className="text-sm text-gray-900 mt-0.5">
                        {s.visual_concept}
                      </p>
                    </div>
                  )}

                  {s.detailed_prompt && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("Prompt para imagem")}
                      </span>
                      <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">
                        {s.detailed_prompt}
                      </p>
                    </div>
                  )}

                  {s.color_palette && s.color_palette.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                        <Palette className="h-3.5 w-3.5" />{" "}
                        {t("Paleta de cores")}
                      </span>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {s.color_palette.map((color, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white border border-purple-200 text-gray-800"
                          >
                            {color}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {s.key_elements && s.key_elements.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("Elementos visuais")}
                      </span>
                      <ul className="mt-1 list-disc list-inside text-sm text-gray-900 space-y-0.5">
                        {s.key_elements.map((el, i) => (
                          <li key={i}>{el}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {s.composition_style && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("Estilo de composição")}
                      </span>
                      <p className="text-sm text-gray-900 mt-0.5">
                        {s.composition_style}
                      </p>
                    </div>
                  )}

                  {s.text_overlay_suggestion && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                        <Type className="h-3.5 w-3.5" />{" "}
                        {t("Texto sugerido para overlay")}
                      </span>
                      <p className="text-sm text-gray-900 mt-0.5">
                        {s.text_overlay_suggestion}
                      </p>
                    </div>
                  )}

                  {s.emotional_tone && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("Tom emocional")}
                      </span>
                      <p className="text-sm text-gray-900 mt-0.5">
                        {s.emotional_tone}
                      </p>
                    </div>
                  )}

                  {s.technical_specs && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("Especificações técnicas")}
                      </span>
                      <p className="text-sm text-gray-900 mt-0.5">
                        {s.technical_specs}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-500 pt-2">
          {t(
            "Use essas sugestões ao criar ou melhorar a imagem do post (Gerar imagem / Melhorar imagem).",
          )}
        </p>
      </div>
    </Modal>
  );
};

export default VisualSuggestionsModal;
